import React from 'react';
import {
  Box, Card, CardActionArea, Typography, Drawer, IconButton, Divider, useMediaQuery, Chip
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Close as CloseIcon, Star as StarIcon } from '@mui/icons-material';
import { tokens, outcomeLabel } from '../theme';
import {
  TeamLine, ProbabilityBar, OutcomePill, StatusBadge, probabilitiesOf, pct, formatTime, TeamCrest
} from './ui';

const STRONG = 0.6;

export function MatchCard({ prediction: p, live, onOpen }) {
  const probabilities = probabilitiesOf(p);
  const strong = p.confidence >= STRONG;
  const showScore = live && (live.is_live || live.is_finished);

  return (
    <Card sx={{
      height: '100%',
      transition: 'border-color .2s, transform .2s',
      borderColor: live?.is_live ? alpha(tokens.status.live, 0.5) : tokens.border,
      '&:hover': { borderColor: tokens.borderStrong },
    }}>
      <CardActionArea onClick={() => onOpen?.(p)} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} gap={1}>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
            {p.league}
          </Typography>
          {live && (live.is_live || live.is_finished)
            ? <StatusBadge live={live} />
            : <Typography variant="caption" color="text.secondary" flexShrink={0}>{formatTime(p.kickoff_time)}</Typography>}
        </Box>

        <Box display="flex" flexDirection="column" gap={1} mb={2}>
          <TeamLine name={p.home_team} logo={p.home_logo} score={showScore ? live.home_score : undefined} highlight={p.prediction === 'home'} />
          <TeamLine name={p.away_team} logo={p.away_logo} score={showScore ? live.away_score : undefined} highlight={p.prediction === 'away'} />
        </Box>

        <Box mt="auto">
          <ProbabilityBar probabilities={probabilities} pick={p.prediction} />
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={1.75} gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <OutcomePill outcome={p.prediction} label={p.prediction === 'draw' ? 'Draw' : `${outcomeLabel[p.prediction]} win`} />
              {strong && (
                <Box display="inline-flex" alignItems="center" gap={0.25} sx={{ color: 'primary.main', fontSize: 12, fontWeight: 700 }}>
                  <StarIcon sx={{ fontSize: 14 }} /> Favourite
                </Box>
              )}
            </Box>
            <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: '1.05rem' }}>
              {pct(p.confidence)}
            </Typography>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
}

function OddsCell({ outcome, odds, probability, picked }) {
  const fair = 1 / probability;
  return (
    <Box sx={{
      flex: 1, p: 1.5, borderRadius: 2, textAlign: 'center',
      bgcolor: picked ? alpha(tokens.outcome[outcome], 0.12) : tokens.surfaceRaised,
      border: `1px solid ${picked ? alpha(tokens.outcome[outcome], 0.5) : tokens.border}`,
    }}>
      <Typography variant="overline" color="text.secondary">{outcomeLabel[outcome]}</Typography>
      <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: '1.35rem', lineHeight: 1.2 }}>
        {Number(odds).toFixed(2)}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">{pct(probability, 1)}</Typography>
      <Typography variant="caption" color="text.secondary" display="block">fair {fair.toFixed(2)}</Typography>
    </Box>
  );
}

function Row({ label, children }) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" py={1} gap={2}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600} textAlign="right">{children}</Typography>
    </Box>
  );
}

// Right-hand panel on desktop, bottom sheet on phones
export function PredictionDrawer({ prediction: p, live, onClose }) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const open = !!p;
  const probabilities = p ? probabilitiesOf(p) : null;
  const stats = p?.reddit_stats;
  const margin = p?.bookmaker_margin ?? (p ? (1 / p.home_odds + 1 / p.draw_odds + 1 / p.away_odds - 1) : 0);

  return (
    <Drawer
      anchor={mobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{ sx: {
        width: mobile ? '100%' : 440,
        maxHeight: mobile ? '88vh' : '100%',
        borderTopLeftRadius: mobile ? 20 : 0,
        borderTopRightRadius: mobile ? 20 : 0,
        bgcolor: tokens.surface,
        borderLeft: mobile ? 'none' : `1px solid ${tokens.border}`,
      } }}
    >
      {p && (
        <Box p={{ xs: 2.5, sm: 3 }} pb={{ xs: 'calc(env(safe-area-inset-bottom) + 24px)', sm: 3 }}>
          {mobile && <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: tokens.borderStrong, mx: 'auto', mb: 2 }} />}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="overline" color="text.secondary">{p.league}</Typography>
              <Typography variant="body2" color="text.secondary">
                {new Date(p.kickoff_time || p.date).toLocaleString(undefined, { weekday: 'long', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Box>
            <IconButton onClick={onClose} aria-label="Close" size="small" sx={{ mr: -1 }}><CloseIcon /></IconButton>
          </Box>

          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={3}>
            {[['home', p.home_team, p.home_logo], ['away', p.away_team, p.away_logo]].map(([side, name, logo], i) => (
              <React.Fragment key={side}>
                {i === 1 && (
                  <Box textAlign="center" flexShrink={0}>
                    {live && (live.is_live || live.is_finished) ? (
                      <>
                        <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: '2rem', lineHeight: 1 }}>
                          {live.home_score}–{live.away_score}
                        </Typography>
                        <Box mt={0.5}><StatusBadge live={live} /></Box>
                      </>
                    ) : (
                      <Typography variant="h5" color="text.secondary">vs</Typography>
                    )}
                  </Box>
                )}
                <Box flex={1} minWidth={0} textAlign="center" display="flex" flexDirection="column" alignItems="center" gap={1}>
                  <TeamCrest src={logo} name={name} size={52} />
                  <Typography variant="subtitle2" sx={{ lineHeight: 1.25 }}>{name}</Typography>
                </Box>
              </React.Fragment>
            ))}
          </Box>

          <Box sx={{ p: 2, borderRadius: 3, bgcolor: tokens.surfaceRaised, border: `1px solid ${tokens.border}`, mb: 2.5 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Box>
                <Typography variant="overline" color="text.secondary">Prediction</Typography>
                <Box><OutcomePill outcome={p.prediction} label={p.prediction === 'draw' ? 'Draw' : `${p.prediction === 'home' ? p.home_team : p.away_team} win`} /></Box>
              </Box>
              <Box textAlign="right">
                <Typography variant="overline" color="text.secondary">Probability</Typography>
                <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: '1.6rem', lineHeight: 1.1 }}>
                  {pct(p.confidence, 1)}
                </Typography>
              </Box>
            </Box>
            <ProbabilityBar probabilities={probabilities} pick={p.prediction} height={10} />
          </Box>

          <Typography variant="subtitle2" mb={1}>
            Odds{p.odds_provider ? ` · ${p.odds_provider}` : ''}
          </Typography>
          <Box display="flex" gap={1} mb={1}>
            {['home', 'draw', 'away'].map(k => (
              <OddsCell key={k} outcome={k} odds={p[`${k}_odds`]} probability={probabilities[k]} picked={p.prediction === k} />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={2.5}>
            "Fair" odds remove the bookmaker's {pct(margin, 1)} margin. Probabilities come from the market, so a 60% pick should win about 6 times in 10.
          </Typography>

          <Divider sx={{ mb: 1 }} />
          <Row label="Reddit sentiment">
            {p.reddit_sentiment > 0 ? 'Positive' : p.reddit_sentiment < 0 ? 'Negative' : 'Neutral'}
            {stats ? ` · ${stats.posts_analyzed} posts` : ''}
          </Row>
          {p.result && p.result !== 'expired' && (
            <Row label="Result">
              <Chip size="small" label={p.result === p.prediction ? 'Correct' : 'Missed'} color={p.result === p.prediction ? 'success' : 'error'} variant="outlined" />
            </Row>
          )}
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Sentiment is shown for context only and does not change the prediction.
          </Typography>

          {stats?.sentiment_breakdown?.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" mb={1}>Top Reddit posts</Typography>
              {stats.sentiment_breakdown.map((post, i) => (
                <Box key={i} sx={{ p: 1.25, mb: 1, borderRadius: 2, bgcolor: tokens.surfaceRaised }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.35 }}>{post.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {post.upvotes} upvotes · {post.comments} comments · sentiment {post.sentiment > 0 ? '+' : ''}{post.sentiment}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Drawer>
  );
}
